/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { and, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import Options from '@linc-technologies/linced-common/enums/options';
import {
  byKeys,
  filterBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  loadProgressDialData,
  queryProgressionRecords,
} from '@linc-technologies/linced-common/utils/progress-dial';
import {
  getCurrentMilestone,
  subjectProgressions,
  generateStudentProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import { restartableTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import moment from 'moment';

// LOAD_ARGS are the arguments that goalsTask and progressDialTask read.
const LOAD_ARGS = [
  'subject',
  'person',
  'endTime',
  'isParentView',
  'levels',
  'expectations',
  'progressions',
  'learnerSummary',
  'developingStates',
  'secureStates',
];

export default class SubjectSummary extends Component {
  // Services
  @service
  colours;

  @service
  dates;

  @service
  heroApi;

  @service
  options;

  @service
  session;

  @service
  store;

  // developingStates is a list of recent Goal States for in-progress goals.
  developingStates = null;

  // secureStates is a list of Goal States for recently completed goals.
  secureStates = null;

  // levels specifies the Progress Levels for this Subject.
  levels = null;

  // expectations specifies the Expectations for this Subject.
  expectations = null;

  // progressions specifies the Progressions for this Subject.
  @tracked
  progressions = null;

  // learnerSummary contains a preloaded Learner Summary for this Subject.
  @tracked learnerSummary = null;

  @alias('subject.isGradable')
  showGrader;

  @optionAlias(Options.EXPECTATIONS_PROGRESS_CHARTS)
  showProgressCharts;

  @optionAlias(Options.EXPECTATIONS_PROGRESS_CHART_KEY)
  showProgressChartKey;

  @optionAlias(Options.EXPECTATIONS_SHOW_ACHIEVEMENT_CHIPS)
  _showAchievementChips;

  @optionAlias(Options.EXPECTATIONS_SHOW_ACHIEVEMENT_CHIPS_STAFF)
  _showAchievementChipsStaff;

  @optionAlias(Options.CHARTS_SHOW_Y_AXIS_LABELS)
  showYAxisLabels;

  @optionAlias(Options.CHARTS_HIDE_TOOL_TIP)
  hideTooltip;

  @computed(
    'isParentView',
    '_showAchievementChips',
    '_showAchievementChipsStaff',
  )
  get showAchievementChips() {
    if (this.isParentView) {
      return this._showAchievementChips;
    }
    return this._showAchievementChipsStaff;
  }

  @optionAlias(Options.EXPECTATIONS_SHOW_ACHIEVEMENT_STATEMENTS)
  _showAchievementStatements;

  @optionAlias(Options.EXPECTATIONS_SHOW_ACHIEVEMENT_STATEMENTS_STAFF)
  _showAchievementStatementsStaff;

  @computed(
    'isParentView',
    '_showAchievementStatements',
    '_showAchievementStatementsStaff',
  )
  get showAchievementStatements() {
    if (this.isParentView) {
      return this._showAchievementStatements;
    }
    return this._showAchievementStatementsStaff;
  }

  @optionAlias(Options.EXPECTATIONS_SHOW_CURRENT_EXPECTATION)
  _showCurrentExpectation;

  @optionAlias(Options.EXPECTATIONS_SHOW_CURRENT_EXPECTATION_STAFF)
  _showCurrentExpectationStaff;

  @computed(
    'isParentView',
    '_showCurrentExpectation',
    '_showCurrentExpectationStaff',
  )
  get showCurrentExpectation() {
    if (this.isParentView) {
      return this._showCurrentExpectation;
    }
    return this._showCurrentExpectationStaff;
  }

  // showCurriculumLevel is only used to determine whether to show the Progress
  // card when in parent view; the achievement-statement component handles the
  // actual display, so we don't need to check the staff option here.
  @optionAlias(Options.EXPECTATIONS_INCLUDE_CURRICULUM_LEVEL)
  showCurriculumLevel;

  @optionAlias(Options.CHARTS_SHOW_ALL_COLOURS)
  showAllColours;

  @optionAlias(Options.GOALS_VISIBLE)
  showGoals;

  @optionAlias('parents:goals:showEvidence')
  showEvidence;

  @optionAlias('parents:goals:showGradeLabels')
  showGradeLabel;

  @optionAlias(Options.PROGRESS_REPORTING_ENABLED)
  progressionsEnabled;

  @optionAlias(Options.REPORTING_MINI_GRADED_CARDS)
  _miniGradedCards;

  @and('_miniGradedCards', 'subject.isGradable')
  miniGradedCards;

  @computed('session.activeSchool')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  @optionAlias('progress:reporting:introCurrent')
  introCurrent;

  @computed('introCurrent', 'person.firstName')
  get introCurrentText() {
    const optionText = this.introCurrent || '{{NAME}} is learning to:';
    const firstName = this.get('person.firstName');

    // Replace the placeholder with the actual name
    if (optionText && firstName) {
      return optionText.replace(/{{NAME}}/g, firstName);
    }

    return optionText;
  }

  @optionAlias('progress:reporting:introComplete')
  introComplete;

  @computed('introComplete', 'person.firstName')
  get introCompleteText() {
    const optionText = this.introComplete || '{{NAME}} is able to:';
    const firstName = this.get('person.firstName');

    // Replace the placeholder with the actual name
    if (optionText && firstName) {
      return optionText.replace(/{{NAME}}/g, firstName);
    }

    return optionText;
  }

  @computed('milestones.@each.{sequence,label}')
  get sortedMilestones() {
    return sortBy(this.milestones, 'sequence', 'label');
  }

  @computed(
    'sortedMilestones.[]',
    'person.{weeksAtSchool,monthsAtSchool,currentYearLevel}',
    'endTime',
  )
  get currentMilestone() {
    let asAtDate;
    if (this.endTime) {
      asAtDate = moment(this.endTime * 1000).format('YYYY-MM-DD');
    }
    return getCurrentMilestone(
      this.sortedMilestones,
      this.person,
      this.dates.rolloverMonthDate,
      asAtDate,
    );
  }

  @computed('currentMilestone', 'sortedMilestones.[]')
  get previousMilestone() {
    let currentIndex = this.sortedMilestones.indexOf(this.currentMilestone);
    return this.sortedMilestones[currentIndex - 1];
  }

  @computed('subject.showRadialDials', 'isParentView')
  get showProgressDials() {
    if (this.subject.showRadialDials) {
      // In parent view only actually display if the student is working on or has completed any goals
      return (
        !this.isParentView ||
        !isEmpty(this.developingStates) ||
        !isEmpty(this.secureStates)
      );
    }
    return false;
  }

  @computed('categories.[]', 'subject.id')
  get filteredCategories() {
    if (!isArray(this.categories)) {
      return [];
    }
    return filterBy(this.categories, 'subject.id', this.subject.id);
  }

  @computed(
    'showProgressDials',
    'filteredCategories.length',
    'showRecentGoals',
    'showCurrentGoals',
    'goals.{current,recent}',
    'isParentView',
    'progressDialData',
  )
  get showGoalsCard() {
    let goals = this.goals;
    if (!goals) {
      // The goals are not loaded yet.
      return false;
    }

    // If we should show dials and have data, show the card.
    if (
      this.showProgressDials &&
      (!this.isParentView || !isEmpty(this.progressDialData))
    ) {
      return true;
    }

    // Otherwise, if we don't have any categories, we can't have goals - so don't show it.
    if ((this.get('filteredCategories.length') ?? 0) === 0) {
      return false;
    }

    // If we have developing or secure states, show the card.
    return (
      (this.showCurrentGoals && !isEmpty(goals.current)) ||
      (this.showRecentGoals && !isEmpty(goals.recent))
    );
  }

  @computed(
    'miniGradedCards',
    'progressData',
    'progressionsEnabled',
    'statementData',
    'subject.{trackable,visibleToParents}',
  )
  get showProgressCard() {
    // Don't display if we're displaying as a "mini" card.
    if (this.miniGradedCards) {
      return false;
    }
    // Don't display if not enabled at school and subject level.
    if (!this.progressionsEnabled || !this.get('subject.trackable')) {
      return false;
    }
    // Don't display if there's no data to display.
    if (isEmpty(this.progressData) || isEmpty(this.statementData)) {
      return false;
    }
    // Display when not viewing as caregiver.
    if (!this.isParentView) {
      return true;
    }
    // Display if the subject and at least one element enabled for caregivers.
    return (
      this.get('subject.visibleToParents') &&
      (this.showAchievementStatements ||
        this.showProgressCharts ||
        this.showCurrentExpectation ||
        this.showCurriculumLevel)
    );
  }

  @computed('showProgressDials', 'subject.trackable', 'progressData')
  get showProgressSliders() {
    return (
      !this.showProgressDials && this.subject.trackable && this.progressData
    );
  }

  @computed('dates.rolloverDate', 'endTime')
  get rolloverTimeMillis() {
    if (this.endTime) {
      let endTimeDate = moment(this.endTime * 1000).format('YYYY-MM-DD');
      return moment(this.dates.previousRolloverDate(endTimeDate)).unix() * 1000;
    }
    return moment(this.dates.rolloverDate).unix() * 1000;
  }

  // loadsGoals is true when no goal states are given, so goalsTask loads them.
  get loadsGoals() {
    return isEmpty(this.developingStates) && isEmpty(this.secureStates);
  }

  // goalsTask loads the learner's current goal states for this subject.
  goalsTask = restartableTask(async () => {
    let rolloverTimeMillis = this.rolloverTimeMillis;
    let filters = {
      personId: this.person.id,
      subjectId: this.subject.id,
      current: true,
      include: 'goals',
    };

    if (this.endTime) {
      filters.asAtTimeMillis = this.endTime * 1000;
    }

    let states = await this.store.query('goals/state', filters);
    return {
      current: states.filter((state) => state.awarded === GoalState.DEVELOPING),
      recent: states.filter((state) => {
        return (
          state.awarded === GoalState.SECURE &&
          state.createTime > rolloverTimeMillis
        );
      }),
    };
  });

  // goals is undefined until goalsTask loads the goal states.
  @computed(
    'developingStates.[]',
    'secureStates.[]',
    'goalsTask.lastSuccessful.value',
  )
  get goals() {
    if (this.loadsGoals) {
      return this.goalsTask.lastSuccessful?.value;
    }
    return { current: this.developingStates, recent: this.secureStates };
  }

  @computed('goals.{current.[],recent.[]}')
  get gradedGoals() {
    let recentStates = this.get('goals.recent').filter(
      (state) => state.createTime >= this.rolloverTimeMillis,
    );
    let allRelevantStates = [];
    allRelevantStates.push(...this.get('goals.current'));
    allRelevantStates.push(...recentStates);

    let mostRecentGoal = allRelevantStates.sort(byKeys('createTime')).at(-1);
    // Only consider recentStates for grade calculation
    let recentGrades = recentStates
      .map((state) => state.grade)
      // Grades are 1 to 5. 0 (the Go zero value) means no grade.
      .filter((grade) => grade > 0);
    let total = recentGrades.reduce((sum, grade) => sum + grade, 0);
    let averageGrade =
      recentGrades.length > 0 ? total / recentGrades.length : 0;
    let roundedUpAverageGrade = Math.round(averageGrade);
    return {
      current: this.get('goals.current'),
      recent: recentStates,
      average: roundedUpAverageGrade,
      mostRecentGoal,
    };
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'expectations',
    'recordsTask.lastSuccessful',
  )
  get expectationRecords() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    if (isArray(this.expectations)) {
      // Expectations have been provided; use them.
      return this.expectations;
    }

    // Fetched, as we weren't given any.
    return this.recordsTask.lastSuccessful?.value?.expectations ?? [];
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'progressions',
    'recordsTask.lastSuccessful',
  )
  get progressionRecords() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    if (isArray(this.progressions)) {
      // Progressions have been provided; use them.
      return this.progressions;
    }

    // Fetched, as we weren't given any.
    return this.recordsTask.lastSuccessful?.value?.progressions ?? [];
  }

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'person.id',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
  )
  get progressData() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    let milestones = sortBy(this.milestones, 'sequence', 'label');
    let levels = this.levels;
    let expectations = this.expectationRecords;
    if (isEmpty(milestones) || isEmpty(levels) || isEmpty(expectations)) {
      // We need Milestones, Levels and Expectations to display anything.
      return;
    }

    // Generate progression data for this Subject
    let progressions = this.progressionRecords;
    if (!isEmpty(progressions)) {
      return generateStudentProgressData(
        expectations,
        milestones,
        levels,
        progressions,
        this.showAllColours,
      );
    }
  }

  // recordsTask queries the expectations and progressions that the caller
  // does not give.
  recordsTask = restartableTask(async () => {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }
    return await queryProgressionRecords(
      this.store,
      {
        goalSubjectId: this.subject.id,
        personId: this.person.id,
        asAtTime: this.endTime,
      },
      {
        expectations: !isArray(this.expectations),
        progressions: !isArray(this.progressions),
      },
    );
  });

  // progressDialTask loads the records, then the progress dial data.
  // Arguments and a saved grade change them, so load and updateRecords
  // perform it.
  progressDialTask = restartableTask(async () => {
    await this.recordsTask.perform();
    return await loadProgressDialData(
      this,
      this.session.activeSchool,
      this.person.id,
      this.endTime,
    );
  });

  // load performs the tasks on the first render, and again when an argument
  // that they read changes.
  load = modifier(() => {
    this.getProperties(LOAD_ARGS);
    if (this.loadsGoals) {
      this.goalsTask.perform();
    }
    this.progressDialTask.perform();
  });

  get progressDialData() {
    return this.progressDialTask.lastSuccessful?.value;
  }

  // statementData needs generating if we're not also displaying progress dials.
  @computed(
    'subject.{id,trackable}',
    'showProgressDials',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
    'displayData',
    'progressDialData',
  )
  get statementData() {
    if (!this.subject.trackable) {
      return;
    }
    if (this.showProgressDials) {
      return this.progressDialData;
    }

    if (isEmpty(this.levels) || isEmpty(this.expectationRecords)) {
      // We need Levels and Expectations to display anything.
      return;
    }

    let { expectation, progression } = this.displayData;
    return subjectProgressions(
      this.subject,
      this.levels,
      expectation,
      progression,
    );
  }

  // statementMilestone returns the milestone to display in the achievement/expectation statement.
  @alias('displayData.milestone')
  statementMilestone;

  // displayData returns the expectation, progression and milestone to be displayed in statements etc.
  @computed(
    'subject.id',
    'person.id',
    'levels.[]',
    'expectationRecords.[]',
    'progressionRecords.[]',
  )
  get displayData() {
    let milestone = this.currentMilestone;
    let expectation = this.expectationRecords.find(
      (record) => record.milestone_id === milestone.id,
    );
    let progression = this.progressionRecords.find(
      (record) => record.milestone_id === milestone.id,
    );

    // Use previous milestone if there's no data for the current one (but is for the previous).
    if (isEmpty(progression) && !isEmpty(this.previousMilestone)) {
      let prevMilestone = this.previousMilestone;
      let prevExpectation = this.expectationRecords.find(
        (record) => record.milestone_id === prevMilestone.id,
      );
      let prevProgression = this.progressionRecords.find(
        (record) => record.milestone_id === prevMilestone.id,
      );
      if (!isEmpty(prevProgression)) {
        milestone = prevMilestone;
        expectation = prevExpectation;
        progression = prevProgression;
      }
    }

    return { expectation, progression, milestone };
  }

  @computed('isParentView')
  get numRecentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:numRecentGoals') || 5
      : 5;
  }

  @computed('isParentView')
  get numCurrentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:numCurrentGoals') || 5
      : undefined;
  }

  @computed('isParentView')
  get showRecentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:showSecure')
      : true;
  }

  @computed('isParentView')
  get showCurrentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:showCurrent')
      : true;
  }

  @computed('isParentView')
  get showGoalProgress() {
    return this.isParentView
      ? this.options.value('parents:goals:showGoalProgress')
      : true;
  }

  isExpanded = false;
  @tracked
  editGoalsDialog = false;
  @tracked
  showRecentGoalsDialog = false;

  @tracked
  recentGoals;

  @tracked
  recentGoalsSubject;

  @action
  openRecentGoals(subject, goals) {
    this.recentGoalsSubject = subject;
    this.recentGoals = goals;
    this.showRecentGoalsDialog = true;
  }

  @action
  hideRecentGoals() {
    this.showRecentGoalsDialog = false;
  }

  @action
  showEditGoalsDialog() {
    this.editGoalsDialog = true;
  }

  @action
  hideEditGoalsDialog() {
    this.editGoalsDialog = false;
  }

  @action
  updateRecords() {
    this.progressions = null;
    this.progressDialTask.perform();
  }
}
